import toast from 'react-hot-toast'
import { FlaskConical, KeyRound, RefreshCw, Trash2 } from 'lucide-react'
import { opcionesAlcanceSiembra, ayudaAlcance } from '@/lib/cd2bSiembra'
import DatosAlEntregar from '@/components/cd2b/DatosAlEntregar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { IrAPaso } from './TarjetaDemo'

/**
 * DATOS DE PRUEBA. Sembrar datos para enseñar el sistema es normal; entregarlos mezclados
 * con los reales, no. La decisión de limpiarlos o conservarlos bloquea la puesta en
 * producción, así que vive aquí, a la vista, y no en un aviso que nadie lee.
 */
export default function TarjetaDatosPrueba({ c, onIrAPaso }) {
  const {
    id, form, propuesta, giroSel, tieneDemo, alcanceSiembra, sembrando, cargas, cargasCargando,
    vaciado, chequeoSiembra, credenciales, reversion,
  } = c

  return (
    <Card>
      <CardHeader><CardTitle className="text-h3">Datos de prueba</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
          Lo que siembres aquí (y lo que importes del sistema anterior) queda agrupado en cargas
          reversibles. Antes de pasar la empresa a producción hay que decidir qué se hace con ellas.
        </p>

        {/* Profundidad de la siembra. El motor rico existe y está cableado; hasta ahora
            la pantalla pedía siempre el catálogo mínimo y no había forma de alcanzarlo.
            La CARTA sólo aparece en los rubros que tienen una (hoy, restaurante). */}
        <div className="space-y-1">
          <label className="text-body-sm font-medium text-neutral-700 dark:text-neutral-300">Qué sembrar</label>
          <Select value={alcanceSiembra} onValueChange={c.setAlcanceSiembra}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {opcionesAlcanceSiembra(form.giro).map((o) => (
                <SelectItem key={o.valor} value={o.valor}>{o.etiqueta}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            {ayudaAlcance(form.giro, alcanceSiembra)}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={c.cargarDatosPrueba} disabled={cargasCargando}>
            <RefreshCw size={16} className="mr-1.5" /> {cargasCargando ? 'Consultando…' : 'Ver cargas'}
          </Button>
          {/* Sin giro no hay plantilla que sembrar: el servidor rechazaría con SIN_GIRO. */}
          <Button variant="outline" onClick={() => c.sembrarDatos()} disabled={!form.giro || sembrando}>
            <FlaskConical size={16} className="mr-1.5" />
            {sembrando ? 'Sembrando…' : 'Sembrar datos de ejemplo'}
          </Button>
          {/* Lo que el asesor le entrega al prospecto: con qué usuario y PIN entra a
              cada puesto. Se recalcula en el servidor, no se guarda ningún secreto. */}
          <Button variant="outline" onClick={c.verCredenciales} disabled={!tieneDemo}>
            <KeyRound size={16} className="mr-1.5" /> Ver accesos de la demo
          </Button>
        </div>
        {!form.giro && (
          <p className="text-caption text-warning-700 dark:text-warning-300">
            Elige el giro del negocio (<IrAPaso n={1} onIr={onIrAPaso}>paso 1, Cliente y país</IrAPaso>) para poder sembrar sus datos.
          </p>
        )}
        {/* El tenant se creó con el giro de entonces. Sembrar otro rubro encima mezcla
            dos negocios en la misma empresa: justo los datos incoherentes que se evitan. */}
        {form.giro && propuesta?.giro && propuesta.giro !== form.giro && (
          <Alert variant="warning">
            <AlertDescription>
              La empresa demo se creó con el giro <strong>{propuesta.giro}</strong> y la cotización
              dice ahora <strong>{form.giro}</strong>. Sembrar mezclaría dos rubros en el mismo
              tenant: guarda la cotización y vuelve a crear la demo, o siembra sobre una limpia.
            </AlertDescription>
          </Alert>
        )}

        {/* La empresa ya tiene datos. Se pregunta qué hacer con ellos EN VEZ de sembrar
            encima y avisar después de que la limpieza quedará a medias. */}
        {vaciado && (
          <div className="space-y-2 rounded-md border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 p-3">
            <p className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50">
              Esta empresa de prueba ya tiene {vaciado.total} fila(s) de operación
            </p>
            <p className="text-caption text-neutral-600 dark:text-neutral-400">
              {(vaciado.porTabla || [])
                .slice(0, 6)
                .map((t) => `${t.filas} ${t.tabla.replace('cd2b_', '').replace(/_/g, ' ')}`)
                .join(' · ')}
              {(vaciado.porTabla || []).length > 6 ? ' …' : ''}
            </p>
            <p className="text-body-sm text-neutral-700 dark:text-neutral-300">
              Sembrar encima no queda limpio: el seeder reusa lo que encuentra, y los movimientos
              de lo que ya existía no se podrán borrar después. Si estos datos son de una siembra
              vieja o de pruebas tuyas, lo normal es empezar de cero.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={c.limpiarYSembrar}>
                <Trash2 size={16} className="mr-1.5" /> Vaciar y sembrar limpio
              </Button>
              <Button variant="outline" size="sm" onClick={() => { c.setVaciado(null); c.sembrarDatos({ omitirChequeo: true, omitirVaciado: true }) }}>
                Sembrar encima igual
              </Button>
              <Button variant="ghost" size="sm" onClick={() => c.setVaciado(null)}>Cancelar</Button>
            </div>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Vaciar conserva la configuración, el emisor y los usuarios: la empresa sigue en pie.
              Solo funciona mientras la empresa esté en prueba; una ya entregada no se toca.
            </p>
          </div>
        )}

        {/* Chequeo previo. Dos salidas por cada brecha, y las dos explícitas: ajustar el
            paquete con el precio delante, o sembrar sabiendo qué se pierde. */}
        {chequeoSiembra && (
          <div className="space-y-2 rounded-md border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 p-3">
            {/* El titular depende de si se PIERDE algo. Un paquete correcto que solo deja
                fuera un módulo recomendado no puede anunciarse como insuficiente: es la
                frase que empujaba a subir la cuota sin motivo. */}
            <p className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50">
              {chequeoSiembra.listo
                ? `El paquete cubre lo que la plantilla de «${giroSel?.label || chequeoSiembra.giro}» escribe. Hay extras recomendados.`
                : `La plantilla de «${giroSel?.label || chequeoSiembra.giro}» pide más de lo que este paquete vende`}
            </p>
            <ul className="text-body-sm text-neutral-700 dark:text-neutral-300 space-y-1">
              {chequeoSiembra.brechas.map((b) => (
                <li key={`${b.tipo}-${b.clave}`}>
                  <span className="font-medium">
                    {b.tipo !== 'area'
                      ? `Faltan ${b.faltan} asiento(s) ${b.clave === 'completo' ? 'completos' : 'operativos'} (pide ${b.pide}, hay ${b.hay})`
                      : b.origen === 'sugerido'
                        ? `Recomendado para el rubro: módulo de ${b.clave.replace(/_/g, ' ')}`
                        : `Falta el módulo de ${b.clave.replace(/_/g, ' ')}`}
                  </span>
                  <span className="block text-caption text-neutral-600 dark:text-neutral-400">{b.efecto}</span>
                </li>
              ))}
            </ul>
            {chequeoSiembra.precio?.delta > 0 && (
              <p className="text-body-sm text-neutral-800 dark:text-neutral-200">
                Añadir los módulos que faltan sube el paquete a{' '}
                <strong>${chequeoSiembra.precio.ajustado?.toFixed(2)}/mes</strong>{' '}
                (<strong>+${chequeoSiembra.precio.delta?.toFixed(2)}</strong>)
                {chequeoSiembra.precio.comision > 0 && (
                  <>, y tu comisión a ${chequeoSiembra.precio.comision.toFixed(2)}/mes</>
                )}
                . Los asientos se amplían con add-ons de usuario, en el <IrAPaso n={2} onIr={onIrAPaso}>paso 2</IrAPaso>.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => {
                  // Marca los módulos que faltan en el selector del paso 2; el precio se
                  // recalcula solo con el motor de la cotización. Guardar sigue siendo un
                  // acto explícito del asesor: aquí no se cobra nada a escondidas.
                  const faltan = chequeoSiembra.ajuste?.modulos || []
                  if (faltan.length > 0) {
                    c.sumarModulos(faltan)
                    toast.success('Módulos añadidos al paquete. Revisa el total y guarda la cotización.')
                  }
                  c.setChequeoSiembra(null)
                }}
                disabled={!(chequeoSiembra.ajuste?.modulos || []).length}
              >
                Ajustar el paquete
              </Button>
              <Button variant="outline" size="sm" onClick={() => { c.setChequeoSiembra(null); c.sembrarDatos({ omitirChequeo: true }) }}>
                Sembrar así, sabiendo qué falta
              </Button>
              <Button variant="ghost" size="sm" onClick={() => c.setChequeoSiembra(null)}>Cancelar</Button>
            </div>
          </div>
        )}

        {/* Accesos por puesto. El prospecto entra con estos y comprueba en vivo que un
            cajero no ve lo mismo que un gerente: es media demostración por sí sola. */}
        {credenciales !== null && credenciales.length > 0 && (
          <div className="space-y-1 rounded-md border border-line p-3">
            <p className="text-body-sm font-medium text-neutral-800 dark:text-neutral-200">Accesos de la empresa demo</p>
            <ul className="text-body-sm text-neutral-700 dark:text-neutral-300 space-y-1">
              {credenciales.map((cr) => (
                <li key={`${cr.nombre}-${cr.rol}`} className="flex flex-wrap items-center justify-between gap-x-2">
                  <span>
                    <span className="font-medium">{cr.nombre}</span>
                    <span className="text-neutral-500 dark:text-neutral-400"> — {cr.puesto || cr.rol}</span>
                  </span>
                  {cr.pin ? (
                    <code className="font-mono font-semibold text-neutral-900 dark:text-neutral-50">PIN {cr.pin}</code>
                  ) : cr.tienePin ? (
                    // Entra, pero su PIN no lo puso el seeder: es el dueño que creó el
                    // aprovisionamiento. Se dice dónde está, no se adivina.
                    <span className="text-caption text-neutral-500 dark:text-neutral-400">PIN propio — «Ver acceso del dueño»</span>
                  ) : (
                    <span className="text-caption text-neutral-500 dark:text-neutral-400">sin acceso (no cabe en los asientos)</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Los de escritorio (dueño, contador) entran con su PIN de 6 dígitos; los de la app
              móvil, con el de 4. Quien salga «sin acceso» se sembró como empleado de planilla:
              el plan cotizado no tiene asientos para él. Al dueño de la empresa demo lo creó el
              aprovisionamiento con su propio PIN: ese se recupera con «Ver acceso del dueño».
            </p>
          </div>
        )}

        {cargas !== null && (
          cargas.length === 0 ? (
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              No hay cargas activas: la empresa está limpia y puede entregarse tal cual.
            </p>
          ) : (
            <>
              <ul className="text-body-sm text-neutral-700 dark:text-neutral-300 space-y-1">
                {cargas.map((cg) => (
                  <li key={cg.id} className="flex flex-wrap items-center justify-between gap-2">
                    <span className="min-w-0">
                      <span className="font-medium">{cg.descripcion || cg.tipo || cg.id}</span>
                      {' — '}{cg.filas} fila(s)
                    </span>
                    {/* Borrar para volver a sembrar: el servicio y la ruta existían desde
                        docs/71 P6 y no estaban conectados a ningún botón. */}
                    <Button variant="ghost" size="sm" onClick={() => c.previsualizarReversion(cg)}>
                      <Trash2 size={14} className="mr-1" /> Borrar
                    </Button>
                  </li>
                ))}
              </ul>

              {reversion && (
                <Alert variant="warning">
                  <AlertDescription className="space-y-2">
                    <p>
                      Se eliminará <strong>{reversion.previa?.filas ?? 0}</strong> fila(s) de
                      «{reversion.carga.descripcion || reversion.carga.id}».
                    </p>
                    {(reversion.previa?.porTabla || []).length > 0 && (
                      <p className="text-caption">
                        {(reversion.previa.porTabla || [])
                          .map((t) => `${t.tabla?.replace('cd2b_', '') ?? t.entidad}: ${t.filas}`)
                          .join(' · ')}
                      </p>
                    )}
                    {(reversion.previa?.bloqueos || []).length > 0 && (
                      <p className="text-caption">
                        No se puede borrar todo:{' '}
                        {(reversion.previa.bloqueos || []).map((b) => b.motivo || b).join(' · ')}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" onClick={c.confirmarReversion}>Borrar definitivamente</Button>
                      <Button size="sm" variant="outline" onClick={() => c.setReversion(null)}>Cancelar</Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
              {/* Con todo, sin los de ejemplo o vacía. Antes eran «Limpiar» y «Conservar», y
                  limpiar se llevaba también lo importado del sistema anterior. */}
              <DatosAlEntregar
                propuestaId={id}
                onCambio={() => {
                  c.cargar()
                  c.revisarProduccion({ silencioso: true })
                }}
              />
            </>
          )
        )}
      </CardContent>
    </Card>
  )
}
